import { SearchIcon } from 'assets/icons';
import { last } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export default function DebouncedSearch({
  onChange,
  placeholder = 'Search...',
  className,
  delay = 400,
  value = '',
}: {
  value?: string;
  placeholder?: string;
  onChange: (search: string) => void;
  className?: string;
  delay?: number;
}) {
  const [search, setSearch] = useState(value);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setSearch(value);
    }
  }, [value]);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        if (lastEmitted.current != search) {
          onChange(search);
          lastEmitted.current = search;
        }
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [search, delay], // Only re-call effect if value or delay changes
  );

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      role="group"
      className={`form__input min-w-[250px] xl:max-w-[24rem] flex items-center ${className}`}
      onClick={() => inputRef.current?.focus()}
    >
      <SearchIcon className="w-4 stroke-current text-gray-700 h-4 mr-2" />

      <input
        key={'DebouncedSearch'}
        className="outline-none bg-transparent text-gray-dark focus:outline-none placeholder-black"
        ref={inputRef}
        style={{
          width: search.length === 0 ? '1px' : search.length + 2 + 'ch',
        }}
        value={search}
        placeholder={placeholder}
        onChange={(e) => setSearch(e.target.value)}
      />
    </div>
  );
}
